<template>
  <div>
    <form
      v-if="hasAnySearchFilters"
      class="relative mt-2 flex items-center gap-2"
      @submit.prevent="search"
    >
      <div class="flex w-full items-center gap-3">
        <div
          class="w-full rounded-md border border-gray-200 bg-lighter-gray px-2 py-1 text-left"
        >
          <p class="text-xs text-neutral-subtle-bold">
            {{ searchDisplayTopLineText }}
          </p>

          <p class="text-xs font-normal capitalize text-neutral-subtle">
            {{ searchDisplayBottomLineText }}
          </p>
        </div>
      </div>

      <div class="relative">
        <img
          class="h-5 w-5 cursor-pointer"
          src="@/assets/filter.svg"
          alt="filter icon"
          @click="openFilterBottomSheet"
        />

        <span
          v-if="filtersCount > 0"
          class="absolute -right-1 -top-2 rounded-full bg-yellow-500 px-1.5 text-xs"
        >
          {{ filtersCount }}
        </span>
      </div>
    </form>

    <div
      v-else
      class="relative w-full"
    >
      <div class="flex">
        <DashboardLoadSearch></DashboardLoadSearch>
      </div>
    </div>

    <LoadFilterBottomSheet
      :visible="showFilterBottomSheet"
      @close="closeFilterBottomSheet"
    ></LoadFilterBottomSheet>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import { LoadFilterBottomSheet, DashboardLoadSearch } from '@/components/loads';

const emit = defineEmits(['search-loads']);

const router = useRouter();
const store = useStore();

const searchText = ref('');
const showFilterBottomSheet = ref(false);

const searchFilters = computed(() => store.state.loads?.loadSearchFilters);
const hasAnySearchFilters = computed(() =>
  Object.entries(searchFilters.value).some(
    ([key, value]) =>
      value?.length > 0 &&
      key !== 'origin_deadhead' &&
      key !== 'destination_deadhead'
  )
);

function removeAfterSecondComma(str) {
  const parts = str?.split(',');
  if (parts && parts.length >= 2) {
    return `${parts[0].trim()}, ${parts[1].trim()}`;
  }
  return str;
}

const orderedFiltersTopLine = computed(() => {
  if (!searchFilters.value?.origin && !searchFilters.value?.destination) {
    return null;
  }

  let originText;
  let destinationText;

  if (searchFilters.value?.origin) {
    originText =
      removeAfterSecondComma(searchFilters.value.origin) +
      `${searchFilters.value.origin_deadhead ? ' - ' + searchFilters.value.origin_deadhead : ''}`;
  }
  if (searchFilters.value?.destination) {
    destinationText =
      removeAfterSecondComma(searchFilters.value.destination) +
      `${searchFilters.value.destination_deadhead ? ' - ' + searchFilters.value.destination_deadhead : ''}`;
  }

  return [originText, destinationText];
});

const postingHash = {
  '8hr': '<8 hours',
  '12hr': '8-12 hours',
  '24hr': '12-24 hours',
  '72hr': '24-72 hours',
};

const postingValue = computed(() => {
  if (searchFilters.value?.posting?.length > 0) {
    return postingHash[searchFilters.value.posting[0]];
  }
  return null;
});

const minRateValue = computed(() => {
  if (searchFilters.value?.min_rate) {
    return `$${searchFilters.value.min_rate}+`;
  }
  return null;
});

const dateRangeValue = computed(() => {
  if (searchFilters.value.pickup_start && searchFilters.value.dropoff_start) {
    return `${dayjs(searchFilters.value.pickup_start).format('MMM D')} - ${dayjs(searchFilters.value.dropoff_start).format('MMM D')}`;
  }
  if (searchFilters.value.pickup_start) {
    return dayjs(searchFilters.value.pickup_start).format('MMM D');
  }
  if (searchFilters.value.dropoff_start) {
    return dayjs(searchFilters.value.dropoff_start).format('MMM D');
  }

  return null;
});

const equipmentMap = {
  flatbed: 'Flatbed',
  power_only: 'Power Only',
  reefer: 'Reefer',
  van: 'Van',
};

const equipmentValue = computed(() => {
  if (Array.isArray(searchFilters.value?.equipment)) {
    return equipmentMap[searchFilters.value?.equipment?.[0]];
  }
  return null;
});

const certificationValue = computed(() => {
  if (Array.isArray(searchFilters.value?.certifications)) {
    return searchFilters.value?.certifications[0];
  }
  return null;
});

const statusValue = computed(() => {
  if (Array.isArray(searchFilters.value?.status)) {
    return searchFilters.value?.status[0];
  }
  return null;
});

const weightValue = computed(() => {
  if (searchFilters?.value?.weight) {
    return `${searchFilters?.value?.weight} lbs`;
  }
  return null;
});

const searchQuery = computed(() => {
  if (searchFilters?.value?.searchQuery) {
    return `${searchFilters?.value?.searchQuery}`;
  }
  return null;
});

const orderedFiltersBottomLine = computed(() => [
  equipmentValue?.value,
  dateRangeValue?.value,
  postingValue?.value,
  minRateValue?.value,
  certificationValue?.value,
  statusValue?.value,
  weightValue?.value,
  searchQuery?.value,
]);

const searchDisplayTopLineText = computed(() =>
  orderedFiltersTopLine.value?.reduce((acc, item) => {
    if (item && acc) {
      acc += ` • ${item}`;
    } else if (item) {
      acc += item;
    }
    return acc;
  }, '')
);

const searchDisplayBottomLineText = computed(() =>
  orderedFiltersBottomLine.value?.reduce((acc, item) => {
    if (item && acc) {
      acc += ` • ${item}`;
    } else if (item) {
      acc += item;
    }
    return acc;
  }, '')
);

const filtersForCount = computed(() => ({
  origin: searchFilters?.value?.origin,
  destination: searchFilters?.value?.destination,
  dropoff_start: searchFilters?.value?.dropoff_start,
  pickup_start: searchFilters?.value?.pickup_start,
  equipment: searchFilters?.value?.equipment,
  min_rate: searchFilters?.value?.min_rate,
  origin_deadhead: searchFilters?.value?.origin_deadhead,
  destination_deadhead: searchFilters?.value?.destination_deadhead,
  posting: searchFilters?.value?.posting,
  rate: searchFilters?.value?.rate,
  certifications: searchFilters?.value?.certifications,
  status: searchFilters?.value?.status,
  team: searchFilters?.value?.team,
  weight: searchFilters?.value?.weight,
}));

const filtersCount = computed(
  () =>
    Object.entries(filtersForCount?.value)?.filter(([key, value]) => {
      if (
        key !== 'sort' &&
        key !== 'origin_deadhead' &&
        key !== 'destination_deadhead'
      ) {
        return Array.isArray(value) ? value?.length : value;
      }
    })?.length
);

const search = () => {
  emit('search-loads', searchText.value);

  router.push({
    query: { search: searchText.value },
  });
};

const openFilterBottomSheet = () => {
  showFilterBottomSheet.value = true;
};

const closeFilterBottomSheet = () => {
  showFilterBottomSheet.value = false;
};
</script>
